import { FC, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Input } from '../../Input/Input'
import { Button } from '../../Button/Button'
import { PUT } from '../../../services/api'
import type { TypeSessionOption } from '../../../pages/Hospitality/Content/Content/types'
import { typeSessionOptions as sharedTypeSessionOptions } from '../../../pages/Hospitality/Content/Content/types'
import Swal from 'sweetalert2'
import { i18n } from '../../../translate/i18n'

type Props = {
  show: boolean
  onClose: () => void
  onUpdated: () => void
  contentData: any | null
  typeSessionOptions?: TypeSessionOption[]
  categories?: any[]
  formServices?: any[]
  hotelPostPages?: any[]
  serviceReservations?: any[]
}

enum typeContentEnum {
  PAGE = 'page_dinamic',
  GENERAL = 'general',
  SERVICE = 'service_form',
  SERVICE_RESERVATION = 'service_reservation',
}
enum typeShowContentFor {
  all = 'all',
  onlyLogin = 'guest',
  onlyLogout = 'no_guest'
}

const EditContentModal: FC<Props> = ({
  show,
  onClose,
  onUpdated,
  contentData,
  typeSessionOptions,
  categories,
  formServices,
  hotelPostPages,
  serviceReservations,
}) => {
  const [loading, setLoading] = useState(false)
  const [selectedClassObj, setSelectedClassObj] = useState<any | null>(null)
  const [selectedServiceObj, setSelectedServiceObj] = useState<any | null>(null)
  const [selectedParent, setSelectedParent] = useState<number | null>(null)
  const [selectedChild, setSelectedChild] = useState<number | string | null>(null)
  const [, setSelectedRoomServiceForForm] = useState<number | null>(null)
  const [selectedParentName, setSelectedParentName] = useState<string | null>(null)
  const [selectedChildName, setSelectedChildName] = useState<string | null>(null)
  const [order, setOrder] = useState<string>('0')
  const [showContentTo, setShowContentTo] = useState<typeShowContentFor>(typeShowContentFor.all)

  const [type, setType] = useState<typeContentEnum>(typeContentEnum.GENERAL)

  const resolvedTypeSessionOptions: TypeSessionOption[] =
    typeSessionOptions ?? sharedTypeSessionOptions

  const allOptions = [
    {
      select: `${i18n.t('hospitality_content.create_modal.placeholder_select_content')}`,
      value: 0,
      type: typeContentEnum.GENERAL,
    },
    // backend services
    ...(formServices ?? []).map((s: any) => ({
      select: `${s.title ?? s.name} - ${i18n.t('hospitality_content.messages.form')}`,
      value: s.id,
      type: typeContentEnum.SERVICE,
    })),
    // hotel post pages
    ...(hotelPostPages ?? []).map((p: any) => ({
      select: `${p.title} - ${i18n.t('hospitality_content.messages.page')}`,
      value: p.id,
      type: typeContentEnum.PAGE,
    })),
    // service reservations
    ...(serviceReservations ?? []).map((r: any) => ({
      select: `${r.title ?? r.name} - ${i18n.t(
        'hospitality_content.messages.service_reservation'
      )}`,
      value: r.id,
      type: typeContentEnum.SERVICE_RESERVATION,
    })),
    // static session types
    ...resolvedTypeSessionOptions.map((t: TypeSessionOption) => ({
      ...t,
      type: typeContentEnum.GENERAL,
    })),
  ]

  // Carregar dados do conteúdo quando o modal abre
  useEffect(() => {
    if (show && contentData && categories && categories.length > 0) {
      loadContent()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contentData, categories, formServices, hotelPostPages, serviceReservations])

  // Resetar campos quando o modal fecha
  useEffect(() => {
    if (!show) {
      resetFields()
    }
  }, [show])

  const resetFields = () => {
    setSelectedClassObj(null)
    setSelectedServiceObj(null)
    setSelectedParent(null)
    setSelectedChild(null)
    setSelectedRoomServiceForForm(null)
    setSelectedParentName(null)
    setSelectedChildName(null)
    setOrder('0')
    setShowContentTo(typeShowContentFor.all)
  }

  const loadContent = () => {
    try {
      if (!contentData) {
        return
      }

      // Preencher ordem
      setOrder(contentData.order?.toString() || '0')

      // Encontrar e definir a categoria selecionada
      const foundCategory = categories?.find((c: any) => {
        return c.id === contentData.sessionHotelClassificationId.toString()
      })

      if (foundCategory) {
        const categoryObj = {
          value: foundCategory.id,
          label: foundCategory.name,
        }
        setSelectedClassObj(categoryObj)
        setSelectedParent(foundCategory.id)
        setSelectedParentName(foundCategory.name)
      } else if (contentData.classificationName) {
        // Categoria não encontrada na lista, mas temos o nome do contentData
        const categoryObj = {
          value: contentData.sessionHotelClassificationId,
          label: contentData.classificationName,
        }
        setSelectedClassObj(categoryObj)
        setSelectedParent(contentData.sessionHotelClassificationId)
        setSelectedParentName(contentData.classificationName)
      } else {
        console.error('Categoria não encontrada para ID:', contentData.sessionHotelClassificationId)
      }

      // Definir o conteúdo/serviço selecionado
      if (contentData.typeSession === 'service_form' && contentData.roomServiceId) {
        // É um formulário de serviço
        setSelectedChild(contentData.roomServiceId)
        setSelectedRoomServiceForForm(contentData.roomServiceId)

        const foundService = formServices?.find((s: any) => s.id === contentData.roomServiceId)

        if (foundService) {
          const serviceObj = {
            value: foundService.id,
            label: `${foundService.title} - ${i18n.t('hospitality_content.messages.form')}`,
          }
          setSelectedServiceObj(serviceObj)
          setType(typeContentEnum.SERVICE)
          setSelectedChildName(
            `${foundService.title} - ${i18n.t('hospitality_content.messages.form')}`
          )
        }
      } else if (contentData.typeSession === 'page_dynamic' && contentData.hotelPostPagesId) {
        setSelectedChild(contentData.hotelPostPagesId)

        const foundPage = hotelPostPages?.find((p: any) => p.id === contentData.hotelPostPagesId)

        if (foundPage) {
          const pageObj = {
            value: foundPage.id,
            label: `${foundPage.title} - ${i18n.t('hospitality_content.messages.page')}`,
          }
          setSelectedServiceObj(pageObj)
          setType(typeContentEnum.PAGE)
          setSelectedChildName(
            `${foundPage.title} - ${i18n.t('hospitality_content.messages.page')}`
          )
        }
      } else if (contentData.typeSession === 'service_reservation' && contentData.postId) {
        setSelectedChild(contentData.postId)

        const foundReservation = serviceReservations?.find((r: any) => r.id === contentData.postId)
        if (foundReservation) {
          const reservationObj = {
            value: foundReservation.id,
            label: `${foundReservation.title ?? foundReservation.name} - ${i18n.t(
              'hospitality_content.messages.service_reservation'
            )}`,
          }
          setSelectedServiceObj(reservationObj)
          setType(typeContentEnum.SERVICE_RESERVATION)
          setSelectedChildName(
            `${foundReservation.title ?? foundReservation.name} - ${i18n.t(
              'hospitality_content.messages.service_reservation'
            )}`
          )
        }
      } else if (contentData.typeSession) {
        setSelectedChild(contentData.typeSession)

        const foundType = resolvedTypeSessionOptions.find(
          (t) => t.value === contentData.typeSession
        )

        if (foundType) {
          const typeObj = {
            select: foundType.select,
            value: foundType.value,
            label: foundType.select,
          }
          setSelectedServiceObj(typeObj)
          setSelectedChildName(foundType.select)
        }
      }

      // Definir para quem o conteúdo será mostrado
      if (contentData.showContentTo) {
        setShowContentTo(contentData.showContentTo as typeShowContentFor)
      } else {
        setShowContentTo(typeShowContentFor.all)
      }

    } catch (e) {
      console.error('Error loading content', e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_content.messages.filter_error_title')),
        text: String(i18n.t('hospitality_content.edit_modal.swal.update_error')),
      })
      onClose()
    }
  }

  const handleClassChange = (value: any) => {
    setSelectedClassObj(value)
    setSelectedParent(value?.value || null)
    setSelectedParentName(value?.select || value?.label || null)
  }

  const handleServiceChange = (value: { label: string; value: number | string }) => {
    const find = allOptions.find((opt: any) => opt.select === value?.label)

    setSelectedServiceObj(value)
    setSelectedChild(value?.value || null)
    setSelectedChildName(value?.label || null)

    find && setType(find.type)
    setSelectedChildName(value?.label || null)
    find && setType(find.type)
  }

  const update = async () => {
    try {
      setLoading(true)

      if (!selectedParent) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_content.edit_modal.swal.missing_field_title')),
          text: String(i18n.t('hospitality_content.edit_modal.swal.missing_section')),
        })
        setLoading(false)
        return
      }

      if (!selectedChild) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_content.edit_modal.swal.missing_field_title')),
          text: String(i18n.t('hospitality_content.edit_modal.swal.missing_content')),
        })
        setLoading(false)
        return
      }

      if (Number(order) < 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_content.edit_modal.swal.invalid_field_title')),
          text: String(i18n.t('hospitality_content.edit_modal.swal.invalid_order')),
        })
        setLoading(false)
        return
      }

      const payload: any = {
        sessionHotelClassificationId: selectedParent,
        status: true,
        order: Number(order) || 0,
      }

      switch (type) {
        case typeContentEnum.PAGE:
          payload.typeSession = 'page_dynamic'
          payload.hotelPostPagesId = selectedChild
          break
        case typeContentEnum.GENERAL:
          payload.typeSession = selectedChild
          break
        case typeContentEnum.SERVICE:
          payload.typeSession = 'service_form'
          payload.roomServiceId = selectedChild
          break
        case typeContentEnum.SERVICE_RESERVATION:
          payload.typeSession = 'service_reservation'
          payload.postId = selectedChild
          break
      }

      payload.showContentTo = showContentTo

      const res = await PUT(`/session-hotel/${contentData.id}`, payload)

      setLoading(false)
      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_content.edit_modal.swal.updated_title')),
          text: String(i18n.t('hospitality_content.edit_modal.swal.updated_text')),
        }).then(() => {
          onUpdated()
          onClose()
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_content.messages.filter_error_title')),
          text: String(i18n.t('hospitality_content.edit_modal.swal.update_error')),
        })
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_content.messages.filter_error_title')),
        text: e ?? String(i18n.t('hospitality_content.edit_modal.swal.update_error')),
      })
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{i18n.t('hospitality_content.edit_modal.modal_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='row'>
          <div className='col-12 mb-4'>
            <div className='alert alert-info'>
              <div className='d-flex'>
                <i className='fas fa-info-circle me-3 mt-1'></i>
                <div>
                  <strong>{i18n.t('hospitality_content.edit_modal.alert_title')}</strong>
                  <br />
                  {i18n.t('hospitality_content.edit_modal.alert_subtitle')}
                </div>
              </div>
            </div>
          </div>

          <div className='col-md-6 mb-4'>
            <label className='form-label fw-bold'>
              <i className='fas fa-layer-group me-3 mt-1'></i>
              {i18n.t('hospitality_content.edit_modal.label_section')}
            </label>
            <Input
              inputAttr={{ type: 'select' }}
              options={[
                {
                  select: String(i18n.t('hospitality_content.edit_modal.placeholder_select')),
                  value: 0,
                },
                ...(categories ?? []).map((c: any) => ({ select: c.name, value: c.id })),
              ]}
              value={selectedClassObj}
              placeholder={String(
                i18n.t('hospitality_content.edit_modal.placeholder_select_section')
              )}
              search={true}
              change={handleClassChange}
            />
          </div>

          <div className='col-md-6 mb-4'>
            <label className='form-label fw-bold'>
              <i className='fas fa-th-large me-3 mt-1'></i>
              {i18n.t('hospitality_content.edit_modal.label_content')}
            </label>
            <Input
              inputAttr={{ type: 'select' }}
              options={allOptions.filter((opt: any) => opt.value !== selectedClassObj?.value)}
              value={selectedServiceObj}
              placeholder={String(
                i18n.t('hospitality_content.edit_modal.placeholder_select_content')
              )}
              search={true}
              change={handleServiceChange}
              disabled={true}
            />
          </div>

          <div className='col-md-12 mb-4'>
            <Input
              label={'Mostrar conteudo para'}
              value={showContentTo}
              change={(v: string) => setShowContentTo(v as typeShowContentFor)}
              options={[
                { select: 'Todos', value: typeShowContentFor.all },
                { select: 'Apenas clientes com Check-in', value: typeShowContentFor.onlyLogin },
                { select: 'Apenas clientes sem Check-in', value: typeShowContentFor.onlyLogout },
              ]}
              inputAttr={{ type: 'select' }}
            />
          </div>

          <div className='col-md-12 mb-4'>
            <Input
              inputAttr={{ type: 'number' }}
              label={String(i18n.t('hospitality_content.edit_modal.order_label'))}
              value={order}
              change={(v: string) => setOrder(v)}
            />
          </div>

          {selectedParentName && selectedChildName && (
            <div className='col-12 mt-6'>
              <div className='alert alert-success'>
                <div className='d-flex'>
                  <i className='fas fa-check-circle me-3 mt-1'></i>
                  <div>
                    <strong>{i18n.t('hospitality_content.edit_modal.selection_summary')}</strong>
                    <br />
                    <div className='mt-2'>
                      <span className='badge badge-primary me-2'>
                        {i18n.t('hospitality_content.edit_modal.badge_section')}
                      </span>
                      <strong>{selectedParentName}</strong>
                    </div>
                    <div className='mt-1'>
                      <span className='badge badge-primary me-2'>
                        {i18n.t('hospitality_content.edit_modal.badge_content')}
                      </span>
                      <strong>{selectedChildName}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className='col-12'>
            <div className='card'>
              <div className='card-body p-3'>
                <h6 className='card-title mb-2'>
                  <i className='fas fa-lightbulb me-2'></i>
                  {i18n.t('hospitality_content.edit_modal.tips_title')}
                </h6>
                <ul className='mb-0 small text-muted'>
                  <li>{i18n.t('hospitality_content.edit_modal.tips.item1')}</li>
                  <li>{i18n.t('hospitality_content.edit_modal.tips.item2')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          text={String(i18n.t('hospitality_content.edit_modal.buttons.cancel'))}
          color='secondary'
          size='small'
          click={onClose}
        />
        <Button
          text={String(i18n.t('hospitality_content.edit_modal.buttons.save'))}
          color='primary'
          size='small'
          click={update}
          loading={loading}
          disabled={!selectedClassObj || !selectedServiceObj}
        />
      </Modal.Footer>
    </Modal>
  )
}

export { EditContentModal }
